import type { LocalizedName } from '@numistic/contracts'
import { DataError, useAdminMetaMutation } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Badge, Button, Checkbox, Field, Input } from '@numistic/ui'

import { useErrorText } from '../lib/errors.js'

/** Откуда запись: из seed (исходная), изменённая или созданная в админке. */
export function OriginBadge({ origin, customized }: { origin: string; customized: boolean }) {
  const { t } = useTranslation()
  if (origin === 'admin') return <Badge variant="primary">{t('adminMeta.origin.admin')}</Badge>
  return customized ? (
    <Badge variant="default">{t('adminMeta.origin.customized')}</Badge>
  ) : (
    <Badge variant="muted">{t('adminMeta.origin.seed')}</Badge>
  )
}

export function UsageBadge({ usage }: { usage: number }) {
  const { t } = useTranslation()
  return usage ? (
    <span className="text-xs text-muted-foreground">{t('adminMeta.usage', { count: usage })}</span>
  ) : (
    <span className="text-xs text-muted-foreground">{t('adminMeta.unused')}</span>
  )
}

/** Переключатель «активно»: деактивированное не предлагается, данные остаются. */
export function ActiveToggle({
  label,
  active,
  onChange,
  disabled,
}: {
  label: string
  active: boolean
  onChange: (active: boolean) => void
  disabled?: boolean
}) {
  const { t } = useTranslation()
  return (
    <label className="flex items-center gap-1.5 text-xs">
      <Checkbox
        aria-label={t('adminMeta.activeFor', { name: label })}
        checked={active}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {t('adminMeta.active')}
    </label>
  )
}

/** Удаление доступно только неиспользуемым записям. */
export function RemoveButton({
  label,
  usage,
  onRemove,
}: {
  label: string
  usage: number
  onRemove: () => void
}) {
  const { t } = useTranslation()
  if (usage) return null
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-label={t('adminMeta.removeFor', { name: label })}
      onClick={() => {
        if (window.confirm(t('adminMeta.removeConfirm', { name: label }))) onRemove()
      }}
    >
      ×
    </Button>
  )
}

export function NameInputs({
  value,
  onChange,
}: {
  value: LocalizedName
  onChange: (v: LocalizedName) => void
}) {
  const { t } = useTranslation()
  return (
    <>
      <Field label={t('adminMeta.nameRu')}>
        <Input
          value={value.ru}
          required
          maxLength={200}
          onChange={(e) => onChange({ ...value, ru: e.target.value })}
        />
      </Field>
      <Field label={t('adminMeta.nameEn')}>
        <Input
          value={value.en ?? ''}
          maxLength={200}
          onChange={(e) => onChange({ ...value, en: e.target.value || undefined })}
        />
      </Field>
    </>
  )
}

/** Мутация админки с понятным текстом ошибки. */
export function useAdminAction() {
  const mutation = useAdminMetaMutation()
  return {
    run: (
      group: Parameters<typeof mutation.mutate>[0]['group'],
      op: string,
      input: unknown,
      onSuccess?: () => void,
    ) => mutation.mutate({ group, op, input }, { onSuccess }),
    pending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
  }
}

export function AdminError({ error }: { error: unknown }) {
  const { t } = useTranslation()
  const errorText = useErrorText()
  if (!error) return null
  let text = errorText.request(error)
  if (error instanceof DataError) {
    if (error.code === 'IN_USE') text = t('adminMeta.errors.inUse')
    if (error.code === 'CODE_TAKEN') text = t('adminMeta.errors.codeTaken')
    if (error.code === 'VALIDATION_FAILED' && error.issues.length)
      text = error.issues
        .map((i) => {
          const [key, arg] = i.message.split(':')
          return t(`adminMeta.errors.${key}` as 'adminMeta.errors.inUse', {
            defaultValue: i.message,
            field: arg,
          })
        })
        .join(' ')
  }
  return <Alert variant="destructive">{text}</Alert>
}

export const nextKey = (keys: string[]) =>
  String(Math.max(0, ...keys.map((k) => Number(k) || 0)) + 1).padStart(4, '0')
